import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import SearchableSelect from "../SearchableSelect";
import { bold, num, tableRows } from "../../utils/xlsx";
import {
  ACCOUNTING,
  cellRight,
  defaultPeriod,
  download,
  EmptyState,
  ExportButton,
  LoadingOverlay,
  money,
  PeriodFilter,
  periodLabel,
  sheetHeading,
  validPeriod,
} from "./common";

const VOUCHER_TYPES = ["SALES", "PURCHASE", "RECEIPT", "PAYMENT", "CONTRA", "JOURNAL", "CREDIT_NOTE", "DEBIT_NOTE"];
const label = (code) => (code || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Account statement of any ledger (Tally's "Ledger Vouchers"): opening balance, every voucher with a running balance,
 * closing balance. Used as a page, and embedded as the drill-down from the ledger-wise totals report
 * (`initialLedgerId` + `initialPeriod` then pre-select and load).
 */
const LedgerVouchers = ({ initialLedgerId, initialPeriod }) => {
  const [ledgers, setLedgers] = useState([]);
  const [ledgerId, setLedgerId] = useState(initialLedgerId ? String(initialLedgerId) : "");
  const [period, setPeriod] = useState(() => initialPeriod || defaultPeriod());
  const [voucherType, setVoucherType] = useState("");
  const [search, setSearch] = useState("");
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get(`${ACCOUNTING}/ledgers`, { includeInactive: true })
      .then((data) => setLedgers(Array.isArray(data) ? data : []))
      .catch((error) => toast.error(error.message || "Failed to load ledgers"));
  }, []);

  const load = async (id = ledgerId) => {
    if (!id) {
      toast.error("Select a ledger");
      return;
    }
    if (!validPeriod(period)) return;
    setLoading(true);
    try {
      setStatement(await api.get(`${ACCOUNTING}/ledgers/${id}/statement`, { fromDate: period.fromDate, toDate: period.toDate }));
    } catch (error) {
      toast.error(error.message || "Failed to load ledger vouchers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialLedgerId) load(String(initialLedgerId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLedgerId]);

  const options = useMemo(() => ledgers.map((l) => ({ value: l.id, label: l.name, sublabel: l.groupLabel })), [ledgers]);

  const lines = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (statement?.lines || []).filter(
      (line) =>
        (!voucherType || line.voucherType === voucherType) &&
        (!needle ||
          [line.voucherNumber, line.particulars, line.referenceNumber, line.narration].some((text) => (text || "").toLowerCase().includes(needle)))
    );
  }, [statement, voucherType, search]);

  const filtered = Boolean(voucherType || search.trim());

  const exportExcel = () => {
    const columns = [
      { label: "Date", key: "voucherDate" },
      { label: "Particulars", key: "particulars" },
      { label: "Vch Type", value: (r) => label(r.voucherType) },
      { label: "Vch No", key: "voucherNumber" },
      { label: "Reference", key: "referenceNumber" },
      { label: "Narration", key: "narration" },
      { label: "Debit", key: "debit", format: "money" },
      { label: "Credit", key: "credit", format: "money" },
      { label: "Balance", key: "runningBalance", format: "money", total: false },
      { label: "Dr/Cr", key: "runningSide" },
    ];
    const rows = [
      ...sheetHeading(`Ledger: ${statement.ledgerName}`, `${statement.fromDate} to ${statement.toDate}`),
      ["Opening Balance", "", "", "", "", "", "", "", num(statement.openingBalance), statement.openingSide],
      ...tableRows(columns, lines, { totals: true }),
      [bold("Closing Balance"), "", "", "", "", "", "", "", bold(num(statement.closingBalance)), bold(statement.closingSide)],
    ];
    download(`Ledger ${statement.ledgerName} ${periodLabel({ fromDate: statement.fromDate, toDate: statement.toDate })}`, [
      { name: "Ledger Vouchers", rows, widths: [12, 34, 14, 22, 20, 40, 14, 14, 16, 7] },
    ]);
  };

  return (
    <div className={initialLedgerId ? undefined : "p-4"} style={{ position: "relative" }}>
      <LoadingOverlay show={loading} label="Loading ledger..." />
      {!initialLedgerId ? (
        <div className="page-subtitle" style={{ marginBottom: "12px" }}>
          Every voucher posted to a ledger — any customer, supplier, bank, cash, expense or income account — with opening, running and closing
          balance.
        </div>
      ) : null}
      <div className="filters-bar">
        <SearchableSelect value={ledgerId} onChange={setLedgerId} options={options} placeholder="Select ledger..." style={{ minWidth: "260px" }} />
        <PeriodFilter period={period} onChange={setPeriod} />
        <button className="primary" onClick={() => load()} disabled={loading}>
          {loading ? "Loading..." : "Load"}
        </button>
        <ExportButton onClick={exportExcel} disabled={!statement} />
      </div>

      {statement ? (
        <>
          <div className="filters-bar" style={{ marginTop: "10px" }}>
            <select value={voucherType} onChange={(e) => setVoucherType(e.target.value)} aria-label="Voucher type">
              <option value="">All voucher types</option>
              {VOUCHER_TYPES.map((type) => (
                <option key={type} value={type}>
                  {label(type)}
                </option>
              ))}
            </select>
            <input type="text" placeholder="Search voucher no, particulars, narration" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th style={{ textAlign: "left" }}>Particulars</th>
                  <th>Vch Type</th>
                  <th>Vch No</th>
                  <th style={cellRight}>Debit</th>
                  <th style={cellRight}>Credit</th>
                  <th style={cellRight}>Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={6} style={{ textAlign: "right", fontWeight: 600 }}>
                    Opening Balance
                  </td>
                  <td style={{ ...cellRight, fontWeight: 600 }}>
                    {money(statement.openingBalance)} {statement.openingSide}
                  </td>
                </tr>
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <EmptyState>No vouchers {filtered ? "match the filters" : "in this period"}</EmptyState>
                    </td>
                  </tr>
                ) : (
                  lines.map((line, index) => (
                    <tr key={`${line.voucherId}-${index}`}>
                      <td>{line.voucherDate}</td>
                      <td style={{ textAlign: "left" }} title={line.narration || ""}>
                        {line.particulars || line.narration || "-"}
                      </td>
                      <td>{label(line.voucherType)}</td>
                      <td>{line.voucherNumber}</td>
                      <td style={cellRight}>{Number(line.debit) ? money(line.debit) : ""}</td>
                      <td style={cellRight}>{Number(line.credit) ? money(line.credit) : ""}</td>
                      <td style={cellRight}>
                        {money(line.runningBalance)} {line.runningSide}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ textAlign: "right", fontWeight: 600 }}>
                    {filtered ? "Total (filtered)" : "Total"}
                  </td>
                  <td style={{ ...cellRight, fontWeight: 600 }}>
                    {money(filtered ? lines.reduce((sum, l) => sum + Number(l.debit), 0) : statement.totalDebit)}
                  </td>
                  <td style={{ ...cellRight, fontWeight: 600 }}>
                    {money(filtered ? lines.reduce((sum, l) => sum + Number(l.credit), 0) : statement.totalCredit)}
                  </td>
                  <td />
                </tr>
                <tr>
                  <td colSpan={6} style={{ textAlign: "right", fontWeight: 700 }}>
                    Closing Balance
                  </td>
                  <td style={{ ...cellRight, fontWeight: 700 }}>
                    {money(statement.closingBalance)} {statement.closingSide}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      ) : (
        <EmptyState>Select a ledger and click Load</EmptyState>
      )}
    </div>
  );
};

export default LedgerVouchers;
